import { Field } from '@shopify/react-form';
import clsx from 'clsx';
import update from 'immutability-helper';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { Review } from 'types/review';
import { Response } from 'utilities/api/use-image-upload';

import { DropZone } from 'components/DropZone';
import { InlineError } from 'components/InlineError';
import { Stack } from 'components/Stack';

import { UploadedItem } from './components/UploadedItem';

import styles from './ImageUpload.module.scss';

interface ImageUploadProps {
  field: Field<Review['images']>;
}

interface ExtendedFile {
  id: string;
  file: File;
}

export function ImageUpload({ field }: ImageUploadProps) {
  const [files, setFiles] = useState<ExtendedFile[]>([]);

  const [errors, setErrors] = useState<string[]>([]);
  const [queue, setQueue] = useState<Promise<Response | Error>[]>([]);

  useEffect(() => {
    async function runRequests() {
      if (queue.length === 0) {
        return;
      }

      const result = await Promise.all(queue);

      type RequestResult = {
        errors: string[];
        items: Array<{ image_key: string }>;
      };

      const { errors, items } = result.reduce(
        (acc, item) => {
          if (item instanceof Error) {
            acc.errors.push(item.message);
          } else {
            acc.items.push({ image_key: item.img });
          }

          return acc;
        },
        { errors: [], items: [] } as RequestResult,
      );

      field.onChange([...field.value, ...items]);

      if (errors) {
        setErrors((prevErrors) => [...prevErrors, ...errors]);
      }

      setQueue([]);
    }

    runRequests();
  }, [queue, field]);

  const handleChange = useCallback(
    (_: File[], acceptedFiles: File[], rejectedFiles: File[]) => {
      const filesToUpload = acceptedFiles.slice(0, 5 - files.length);

      setErrors([]);
      setFiles((prevFiles) => [
        ...prevFiles,
        ...filesToUpload.map((file) => ({
          id: Math.random().toString(16).slice(2),
          file,
        })),
      ]);

      const error = getErrorMessage(rejectedFiles);

      error && setErrors((prevErrors) => [...prevErrors, error]);
    },
    [files],
  );

  const handleRemove = useCallback(
    (id: string) => {
      const index = files.findIndex((file) => file.id === id);

      setErrors([]);
      setFiles((prevFiles) => prevFiles.filter((file) => file.id !== id));
      field.onChange(update(field.value, { $splice: [[index, 1]] }));
    },
    [files, field],
  );

  const handleUpload = useCallback((request: Promise<Response | Error>) => {
    setQueue((prevQueue) => [...prevQueue, request]);
  }, []);

  const itemsMarkup = files.map((file) => (
    <UploadedItem
      key={file.id}
      file={file.file}
      id={file.id}
      onRemove={handleRemove}
      onUpload={handleUpload}
    />
  ));

  const dropZoneMarkup =
    files.length < 5 ? (
      <DropZone
        labelHidden
        disabled={files.length > 4}
        accept="image/png, image/jpeg, image/webp"
        label="Images"
        onDrop={handleChange}
      >
        <DropZone.FileUpload />
      </DropZone>
    ) : null;

  const errorMarkup = errors && (
    <InlineError fieldId="">{errors.join(', ')}</InlineError>
  );

  return (
    <Stack vertical>
      <div
        className={clsx(
          styles.ImageUpload,
          files.length > 0 && styles.hasItems,
        )}
      >
        {itemsMarkup}
        {dropZoneMarkup}
      </div>
      {errorMarkup}
    </Stack>
  );
}

function getErrorMessage(files: File[]) {
  if (files.length === 0) {
    return;
  }

  const fileNames = files.map(({ name }) => name).join(', ');

  return `The following files could not be uploaded: ${fileNames}.
    Allowed file types: .png, .jpg, .webp`;
}
